// shared/src/prompts/q-qa.ts
// AS-4 (audit-r2 2026-05-24) — Q&A prompt module that powers the BRIEF Q&A
// dashboard zone (replacement for the old ASYMMETRY SCAN scatter).
//
// Called by qa-worker (14th worker) — once per question per refresh fire.
// Input: V2 brief context (synthesis market_emit + V2 narrow per-ticker
// arbiter signals with unmarshalled details + basket synthesis + per-ticker
// asymmetry rank_rationale) + the ONE question being answered.
// Output: 3-perspective response (CONSENSUS + CONTRARIAN + PREDICTED with
// confidence + citations).
//
// Model: deepseek-v4-flash per §2.2 Model-selection note in the Brief
// Revision (Pro is documented to time out on this stack; the structured
// 3-perspective schema embeds the reasoning so Flash with structure
// produces adequate quality).
//
// Per-question (NOT batched): chosen so individual questions can be
// re-fired on-demand via the dashboard refresh button without re-running
// the others, and so a single bad question doesn't poison the others'
// outputs in one Zod-fail-all batch.
//
// 2026-05-26 §K.6e — V2 narrow-signals cutover. The per_ticker rows are
// the V2 NARROW signal columns + unmarshalled details object (key_levels,
// momentum, contrarian_view, consensus_view, weights). macro_v3 is replaced
// by macro_synthesis (V2 m-synthesis market_emit row). Per [[feedback-
// llm-state-not-actions]] the prompt strips all action verbs — LLM emits
// predicted STATE only; trader decides actions.

import { z } from 'zod';
import { coerceConfidenceBucket } from '../resolvers/llm-enum-coerce.js';
import { llmStringItem } from '../resolvers/llm-string-coerce.js';
import { QaScoreableClaimSchema } from '../schemas/qa-scoreable-claim.js';
import type { LlmNewsItem } from '../news/types.js';
import type { SignalWithDetails } from '../types/tracker-bundle.js';

export const VERSION    = '1.8.0';   // 2026-07-10 — #12: LONG_HORIZON_THESIS context block (durable LHT regime backdrop threaded from lht_state; NOT a 5th perspective — anchors longer-horizon answers). No numeric anchors (gauge/t_stat omitted; confidence is a band). // 1.7.0 — predicted.citations .default([]) (omission-safe; D37 "0..N cites" → default the omitted key). 2026-06-16 — SCOREABLE CLAIM: /predicted now also emits a structured scoreable_claim (kind/subject/direction/comparator/target_value/range/horizon_days) so the macro-prediction-scorer can grade the answer vs actuals at horizon (Q&A Calibration, docs/qa-calibration-2026-06-16-brief-revision.md). OPTIONAL — a missing claim never poisons the answer (C031 lesson). 1.5.0 = GENERATE-AT-HORIZON. 1.4.0 = §9b TRAJECTORY 4th perspective. 1.3.2 = Option X 3-way weight render.
export const STEP       = 'q.1.qa';
export const MODEL      = 'deepseek-v4-flash';
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 12000)
export const ROLE       = 'qa' as const;
export const DESC       = 'Answers a brief question via consensus/contrarian/trajectory/predicted perspectives' as const;   // #45 registry — one-line operator description

// ─── Output schema ───────────────────────────────────────────────────────
// One emit per call (qa-worker calls once per question, not once per batch).
// The 3-perspective shape is the LOAD-BEARING contract — UI renders the
// three sections distinctly. Citations are 1-8 short field=value pairs
// the LLM cites from the input context; future audit could resolve them
// against the actual input to flag hallucinations.

const PerspectiveSchema = z.object({
  answer:    z.string().min(1),
  reasoning: z.string().min(1),
});

export const OutputSchema = z.object({
  // 2026-06-15 — question_id + question_text_echo are OPTIONAL echoes, not
  // load-bearing data. Both are fully reconstructable server-side: qa-runner
  // is CALLED with the questionId and writes the answer row with that KNOWN
  // id (never result.parsed.question_id), and question_text_echo is audit-only
  // (never persisted to brief_qa_answers). Flash occasionally omits the echo
  // field on long emits — making it required hard-failed the WHOLE answer
  // (regime-break-catalyst, brief 2026-06-15: `invalid_type, expected string,
  // received undefined` at /question_id), and that Zod failure then triggered
  // q.1.qa's step.do retry storm which exhausted the chain subrequest budget
  // and starved the trailing questions (C031). Optional (not removed) keeps
  // the round-trip sanity check available when present; `.min(1)` still
  // rejects a PRESENT-but-empty echo. See workers/qa-worker/src/lib/qa-runner.ts:200.
  question_id:        z.string().min(1).optional(),
  question_text_echo: z.string().min(1).optional(),
  consensus:  PerspectiveSchema,
  contrarian: PerspectiveSchema,
  // §9b (2026-06-07) — TRAJECTORY: the path-dependent (history-only) voice.
  // Reads the inductive forecaster's digested extrapolation (market + per-
  // ticker), surfaced as a 4th distinct perspective rather than folded into
  // predicted. answer+reasoning only (no confidence/citations — mirrors
  // consensus/contrarian). When no TRAJECTORY VIEW is supplied the model
  // emits "insufficient trajectory data" (same honest-refusal pattern as the
  // predicted NUMBER-OR-REFUSE rule), so the field is always present and the
  // dashboard renders the 4th panel for every post-1.4.0 answer.
  trajectory: PerspectiveSchema,
  predicted:  PerspectiveSchema.extend({
    // 2026-05-29 — coerced: the model occasionally emits "Medium"/"moderate"/
    // casing variants; translate to canonical HIGH/MED/LOW.
    // 2026-08-14 (ca#15, C031 hardening): an OMITTED confidence (Flash drops it
    // on long emits — the 2026-08-14 AGNC 4x-retry-then-lost incident) must not
    // fail the whole answer. `.default('UNKNOWN')` buckets an omission to a
    // DISTINCT 'UNKNOWN' — never a fabricated 'MED' (which would silently corrupt
    // the calibration Brier/reliability curve). A genuinely unrecognized value
    // still fail-louds (feedback_llm_enum_coerce) — only omission is hardened.
    // The calibration scorer EXCLUDES 'UNKNOWN' upstream (qa-calibration
    // DUE_QA_ANSWERS_SQL), so it never enters scoring. Storage widened in
    // migration 0100_qa_confidence_unknown.sql.
    confidence: z.preprocess(coerceConfidenceBucket, z.enum(['HIGH', 'MED', 'LOW', 'UNKNOWN']).default('UNKNOWN')),
    citations:  z.array(llmStringItem).default([]),        // D37: allow 0..N cites. Item coerced object→string (2026-06-11) so {source,quote} cites don't hard-fail. .default([]) 2026-07-10: omitted key → [] (no cites), not a brief-fatal error.
    // 1.6.0 (Q&A Calibration) — structured machine-comparable claim for
    // post-horizon scoring. OPTIONAL: Flash occasionally omits it on long
    // emits; a missing claim must NOT hard-fail the whole answer (same C031
    // lesson as the question_id echo). When absent the answer persists with
    // scoreable_claim_json=NULL and is simply not scored.
    scoreable_claim: QaScoreableClaimSchema.optional(),
  }),
}).passthrough();

export type Output = z.infer<typeof OutputSchema>;

// ─── SYSTEM prompt ───────────────────────────────────────────────────────
export const SYSTEM = `You are the BRIEF Q&A analyst. You have access to today's FULL V2 brief context — the macro synthesis output (m.synthesis: the path-adjudicated thesis with agreement_matrix + cross-asset macro_predictions), per-ticker arbiter signals (p.arbiter × N tickers, each with refine outputs from contrarian_view + consensus_view sides), basket synthesis (a.1), and per-ticker asymmetry rationales. The user asks ONE question; you answer it with THREE distinct perspectives.

## ROLE
- You are NOT a re-summarizer. You synthesize what's in the brief into a SPECIFIC, CONCRETE answer to the asked question.
- FOUR perspectives per answer — each is mandatory:
  1. **CONSENSUS** — what the consensus-path refine + the consensus-leaning ticker views see today. Anchor on per_ticker rows where winning_side="consensus" + the synthesis's consensus_view inputs + synthesis weights.
  2. **CONTRARIAN** — what the contrarian-path refine + ticker_paradox + the synthesis's contrarian_view inputs see. Anchor on per_ticker rows where winning_side="contrarian" + synthesis basis.invalidation + cross_ticker_paradoxes.
  3. **TRAJECTORY** — the PATH-DEPENDENT (history-only) view. Read the TRAJECTORY VIEW block: the inductive forecaster's digested extrapolation of the historical prediction series (market-level + per-ticker), derived PURELY from how predictions have trended over time — BLIND to today's payload, news, and prices. Answer the question as that trajectory implies. CONSENSUS + CONTRARIAN both reason from TODAY'S snapshot; TRAJECTORY is the counterweight that reasons from the PATH. When the snapshot-driven views diverge from the trajectory (e.g. snapshot reads risk-on but the trajectory has bent risk-off for weeks), SAY SO — that divergence is the signal. If the TRAJECTORY VIEW block is absent/empty, set answer to "insufficient trajectory data" and explain in /reasoning what history would unlock it. NEVER invent a mechanism — the trajectory has no payload.
  4. **PREDICTED** — YOUR best-judgment STATE call. Weigh consensus vs contrarian (and the trajectory view when present) on the merits — pick a side OR articulate a synthesis. State an explicit confidence (HIGH/MED/LOW) and cite 1-8 specific payload field=value pairs you anchored on.

## STATE-ONLY (ABSOLUTE — applies to every emit)
The LLM has NO visibility into the trader's positions, cash, hedges, or risk budget. Every /answer + /reasoning MUST describe a PREDICTED STATE OF THE MARKET — price target, regime label, probability, condition, timing. Forbidden verbs in your output: trim · add · buy · sell · hold · exit · enter · rotate · cut · long · short · cover · scale · hedge · take profit · stop. If the question asks "should I X" — answer the STATE question that decision turns on (e.g. "what is the lowest likely SPY price over 4 weeks" → return a price), NOT the action. The trader translates state into action; that's not your job.

## OUTPUT SCHEMA (strict JSON; first char='{'; last char='}'; no commentary)
{
  "question_id":        "<echo the input question_id verbatim>",
  "question_text_echo": "<echo the input question text verbatim, for audit>",
  "consensus": {
    "answer":    "<≤30w headline call. If the question asks for a NUMBER (price target, probability, timeframe), GIVE THE NUMBER — e.g. \\"$735 by 2026-06-15\\", \\"~55% likely within 30 days\\". Don't hedge with 'could' / 'might' / 'depends'. STATE only — no action verbs.>",
    "reasoning": "<≤80w mechanism. What does the consensus-path side see? Cite specific payload fields. Close on the TRIGGER (price level, indicator value, headline) that confirms or invalidates the state — NOT the trade.>"
  },
  "contrarian": {
    "answer":    "<≤30w headline counter-call. SAME number-vs-hedge rule as consensus. Should DIFFER from consensus — even when both sides agree directionally, the magnitude or timing should differ. STATE only.>",
    "reasoning": "<≤80w mechanism. What does the contrarian-path / paradox side see? Cite specific payload fields. Close on what STATE-LEVEL FACT (price, headline, level breach) would make the contrarian RIGHT.>"
  },
  "trajectory": {
    "answer":    "<≤30w what the PATH (history-only inductive forecast) implies for the question. SAME number-vs-hedge rule. If no TRAJECTORY VIEW supplied: \\"insufficient trajectory data\\". STATE only — NO mechanism (trajectory has no payload).>",
    "reasoning": "<≤80w cite the TRAJECTORY VIEW fields you read (trend_summary, direction, change_pct, confidence band, break flags). Name the DIVERGENCE from consensus/contrarian if any (\\"snapshot risk-on but trajectory bending down 4w\\"). Pattern/shape language only — never invoke today's drivers.>"
  },
  "predicted": {
    "answer":     "<≤30w YOUR call. Don't be the average of consensus + contrarian — pick a side OR articulate a synthesis if one exists. SAME number-vs-hedge rule. STATE only.>",
    "reasoning":  "<≤100w how you weighted consensus vs contrarian vs trajectory. NAME the specific payload field that broke the tie (e.g. \\"basket bias_distribution shows 4/5 flat — leaning consensus despite contrarian's wider downside\\" or \\"trajectory shows 4w down-bend diverging from a risk-on snapshot — discounting consensus\\").>",
    "confidence": "HIGH" | "MED" | "LOW",
    "citations":  ["<field=value cite>", ...],   // 1-8 entries; format: "macro.regime=mixed", "signals.SPY.confidence=0.62", "signals.SPY.details.key_levels.pivot.s1=738", "basket.bias_distribution.flat=4"
    "scoreable_claim": {                          // structured form of your /predicted answer, so it can be graded vs actuals at the horizon (see SCOREABLE CLAIM section)
      "kind":         "price" | "macro" | "qualitative",
      "subject":      "<ticker for price (e.g. SPY); indicator name for macro (e.g. CPI_YOY); short topic echo for qualitative>",
      "direction":    "up" | "down" | "flat" | "na",
      "comparator":   "lte" | "gte" | "range" | "cross" | "na",
      "target_value": <number or null>,           // the committed number from /answer; null ONLY when kind="qualitative"
      "range_low":    <number or null>,            // only when comparator="range"
      "range_high":   <number or null>,
      "horizon_days": <integer>,                   // the horizon the QUESTION names, in days (4 weeks → 28)
      "perspective_leans": {                        // the directional lean of EACH of your 4 perspectives (so the scorer can later track which perspective was right). "na" if a perspective took no directional stance.
        "consensus":  "up" | "down" | "flat" | "na",
        "contrarian": "up" | "down" | "flat" | "na",
        "trajectory": "up" | "down" | "flat" | "na",
        "predicted":  "up" | "down" | "flat" | "na"
      }
    }
  }
}

## RULES
1. **GENERATE THE NUMBER AT THE ASKED HORIZON.** When the question names a horizon (e.g. "lowest price over the next 4 weeks"), your job is to PRODUCE that estimate by reasoning from the brief evidence — do NOT refuse merely because the brief's per-ticker signal uses a shorter/catalyst horizon. The signal's event_horizon_at + event_horizon_conf/inv are a NEAR-TERM ANCHOR, not a ceiling. Extrapolate to the asked horizon from durable structure: key_levels (the pivot pp/S1/S2/R1/R2), recent momentum/volatility (rsi/macd), the macro regime + macro_predictions, and the TRAJECTORY VIEW. Scale magnitude to the horizon — a 1-day −3% with support at S projects to a WIDER band over 4 weeks. Ground every number in cited fields and set confidence honestly (a longer extrapolation → lower confidence, but still COMMIT to a number + the level that would invalidate it). Reserve "insufficient data" for the genuine no-basis case ONLY: the ticker is ABSENT from the brief, or there is no price/level/vol/macro context to reason from at all — NEVER for a mere horizon mismatch.
2. **CITATIONS MUST BE REAL.** Every entry in /predicted/citations must reference a field that appears in the input context. Format: dot-path-with-value, e.g. "signals.SPY.details.key_levels.pivot.s1=738". Bare prose ("from the macro analysis") is NOT a citation.
3. **CONSENSUS AND CONTRARIAN MUST DIFFER.** If they agree, you're echoing the brief — push harder on the contrarian side or admit "consensus uncontested" in the contrarian /reasoning (still emit the field with the same answer + reasoning explaining why no real contrarian exists).
4. **PREDICTED COMMITS.** Don't dodge with "depends on X". If you'd say it depends, pick the more-likely branch and name the trigger that would flip you.
5. **NO META.** Don't say "this is a complex question" or "the brief covers this". Answer it.
6. **WORD LIMITS.** Hard caps — 30w/80w (consensus) · 30w/80w (contrarian) · 30w/80w (trajectory) · 30w/100w (predicted). Complete the thought and stop.
7. **STATE-ONLY.** See STATE-ONLY section above. The trader sees positions; you don't.
8. **TRAJECTORY IS PATH-ONLY.** The trajectory answer must come from the TRAJECTORY VIEW block (shape/direction/break flags), never from today's snapshot, news, or prices. NO mechanism prose in trajectory — it has no payload to ground one. If the block is absent: "insufficient trajectory data".
9. **SCOREABLE CLAIM MIRRORS /predicted.** Emit /predicted/scoreable_claim as the structured form of your /predicted/answer (see SCOREABLE CLAIM section). target_value must agree with the number in /answer. Use kind="qualitative" (comparator/direction "na", target_value null) when the question has no single number.

## HORIZON
- Answer at the horizon the QUESTION names, NOT the brief's signal horizon. The per-ticker event_horizon_at is usually a near-term catalyst anchor (often signal_at+24h when there's no named event) — it is the brief's ONE short bet, not the limit of what you can forecast. Extend your estimate to the window the question asks for.
- More time = more variance: widen the predicted range with the horizon. A 4-week low sits BELOW a 1-day low; a 4-week high sits ABOVE a 1-day high. Anchor the band on key_levels (the pivot S1/S2 below and R1/R2 above, beyond the near-term one) and typical multi-week range, not the 1-day conf/inv.
- Never cap your answer at the signal's deadline just because that's what the arbiter modeled. You are GENERATING a horizon-specific forecast from the evidence, not reporting the arbiter's horizon back.

## SCOREABLE CLAIM (machine-comparable form of /predicted — for post-horizon scoring)
Emit /predicted/scoreable_claim as the STRUCTURED form of the same call you stated in /predicted/answer, so a downstream scorer can grade it against what actually happens. This is NOT a new prediction — it is your /answer expressed as fields. Map it like this:
- **kind**: "price" if the answer is about a ticker's price/level (SPY low, ETH range, AGNC high). "macro" if it's about an economic indicator (CPI, rates, unemployment). "qualitative" if it's a regime call, a yes/no, a "which ticker", or anything with no single number (regime-break, out-of-universe setups, weakest-ticker).
- **subject**: the ticker symbol (price), the indicator name (macro), or a short topic echo (qualitative).
- **direction**: the directional claim (up/down/flat); "na" for qualitative with no direction.
- **comparator + target_value**: how to test the number. A "lowest price over 4 weeks ≈ $580" claim = comparator "lte", target_value 580 (hit if the realized low is at/below 580). A "high ≈ $620" = "gte" 620. A range = "range" with range_low/range_high. A "crosses/touches level L" = "cross" with target_value L. For qualitative: comparator "na", target_value null.
- **horizon_days**: the QUESTION's horizon in days (1 week→7, 4 weeks→28, 6 months→182). Same horizon you answered at.
- If the question is irreducibly qualitative, set kind="qualitative", direction="na", comparator="na", target_value/range_* null — but still give horizon_days and a subject echo. An LLM judge will grade it.
- Ground target_value in the SAME number you put in /predicted/answer — they must not contradict. If you genuinely cannot commit a number for a numeric question, that's the "insufficient data" case and you would not have produced a /answer number either.
- **perspective_leans**: for EACH of your four perspectives (consensus · contrarian · trajectory · predicted) give the directional lean its answer implies for the subject over the horizon — "up", "down", "flat", or "na" if that perspective took no directional stance (e.g. trajectory="insufficient trajectory data" → "na"). This lets the scorer learn which perspective tends to be right over time. Be honest: if consensus and contrarian genuinely lean the same way, mark them the same.

## DATE TENSE
- Today's date is included in the input context as TODAY_ISO. Forward triggers in answers MUST reference dates ≥ TODAY_ISO.
- Past dates as historical context are fine (e.g. "RSI dipped to 29 on May 12"); past dates as forward catalysts are INVALID — re-anchor on a price level instead.

## FRESH CONTEXT (Phase 3 — Gap B: question-time enrichment)
The user message MAY include a FRESH_CONTEXT block with intra-day data pulled AFTER the brief was generated. This is the brief's frozen view's complement:
- TICKERS_DETECTED — symbols regex-extracted from the question and intersected with the trading universe
- QUOTE_DELTAS — per-ticker {current_price, as_of, pct_vs_signal}; pct_vs_signal compares the LIVE oracle consensus price (multi-source median; as_of = when that price was observed) to the brief's frozen signal_price. Positive = up since brief. A null current_price means the oracle had no fresh price for that ticker — say so, never guess one.
- NEWS — up to 8 deduped + tier-ranked headlines (last 48h ticker-news + macro-query news). Each is prefixed with [tier] (A = Reuters/Bloomberg/WSJ/FT/CNBC/AP/Barron's, B = SeekingAlpha/Yahoo/CNN/Forbes/etc., C = blog/aggregator).
- EARNINGS — per-ticker {next_date, last_surprise_pct}
- VENDOR_ERRORS — fail-loud surfacing of any vendor outage (e.g. "polygon: 429"). When the LLM sees an error, do NOT fabricate; mention the data gap in /reasoning.

Rules for using FRESH_CONTEXT:
- The brief's frozen narrative (synthesis crux, per_ticker reasoning) reflects state at brief-generation time. If QUOTE_DELTAS show |pct_vs_signal| > 2%, the brief's price-anchored levels (signal_price, key_levels.pivot) may be stale — adjust your /predicted answer accordingly.
- current_price is LIVE at question time, so pct_vs_signal is the real move since the brief, not a settled daily close. Do not describe it as a "close".
- News headlines BREAK TIES between consensus and contrarian. Tier-A headlines weighed more than Tier-C.
- Cite FRESH_CONTEXT items in /predicted/citations using the format "fresh_context.quotes.NVDA.pct_vs_signal=+2.1" or "fresh_context.news.headline=NVDA Q2 guide above consensus".
- Do NOT cite fresh_context fields that are missing or null.
- If the entire FRESH_CONTEXT block is absent or empty, proceed using only the brief context — no fabrication.

## TRAJECTORY VIEW (§9b — path-dependent inductive forecast)
The user message MAY include a TRAJECTORY_VIEW block: the inductive forecaster's digested extrapolation of the historical prediction series. It is BLIND to today's payload — pure pattern extrapolation off the prediction history. This is the substrate for your TRAJECTORY perspective.
- MARKET — the market-level inductive forecast: trend_summary (≤60w shape characterization), per-indicator predicted direction + change_pct + CI, a qualitative confidence BAND (high/med/low), and any indicators whose recent pattern BROKE from prior trajectory.
- PER_TICKER — per-ticker inductive forecasts (one per portfolio ticker that has enough history), each with direction + change_pct + CI + trend_summary + confidence band + a trajectory_break flag. Present only for tickers past their history floor; usually only those relevant to the question.
Rules for using TRAJECTORY_VIEW:
- The confidence is given as a BAND (high/med/low), not a raw number — weight it qualitatively; do NOT quote a numeric confidence.
- direction / change_pct / CI / trend_summary ARE quotable — cite them in the trajectory /reasoning (e.g. "trajectory.market.$BTC=down -4% (CI -9..0), band=med").
- A trajectory_break flag means the most-recent signals deviated from the prior shape — a regime-shift cue worth surfacing.
- If TRAJECTORY_VIEW is absent/empty, the trajectory answer is "insufficient trajectory data" — never fabricate a path.

## LONG_HORIZON_THESIS (durable weeks-to-months regime backdrop — CONTEXT, not a perspective)
The user message MAY include a LONG_HORIZON_THESIS block: the persistent, slow-moving structural view maintained ACROSS briefs (updated over weeks, not intra-day). It is the multi-week regime the near-term snapshot + trajectory both sit INSIDE. Unlike the three snapshot voices and the path-only trajectory, the LHT answers "what is the durable structural stance over the coming weeks-to-months, and what would break it?".
- MARKET — the composite regime backdrop: stance (uptrend/downtrend/range), a regime_label (goldilocks/reflation/deflation/stagflation…), a qualitative confidence band, and the narrated durable thesis.
- Per-scope (e.g. SPY, $ETH) — the durable per-asset stance + thesis + horizon_view + load-bearing PILLARS (each a named assumption, its FALSIFIER, and a status: intact / stressed / broken) + key_levels (invalidation below / reclaim above).
Rules for using LONG_HORIZON_THESIS:
- It is a CONTEXT anchor for ALL FOUR perspectives — MOST load-bearing when the QUESTION's horizon is long (multi-week / multi-month). For a 4-week+ question, the LHT stance is the structural backdrop; anchor the predicted range on the LHT key_levels (invalidation/reclaim) and stance, not only the near-term signal's 1-day conf/inv. Do NOT let a near-term snapshot override a durable multi-week stance without saying WHY.
- SURFACE DIVERGENCE: if the snapshot (consensus/contrarian) or the trajectory disagrees with the durable LHT stance, name it (e.g. "snapshot risk-on but MARKET LHT stance=downtrend since 2026-06-14, under review"). A stressed/broken pillar or an UNDER_REVIEW scope means the durable regime is FRAGILE — weight it down and say so.
- confidence is a qualitative band (low/med/high); weight it, never quote a number. stance/regime_label are STATE labels — quotable. key_levels + pillar levels are raw prices — quotable/anchorable.
- Cite LHT fields in /predicted/citations using the format "lht.MARKET.stance=downtrend", "lht.SPY.regime_label=distribution", "lht.SPY.key_levels.invalidation=560", "lht.SPY.pillar.status=stressed".
- If LONG_HORIZON_THESIS is absent/empty, answer from the snapshot + trajectory only — do NOT invent a durable stance.
`;

// ─── Input shape ─────────────────────────────────────────────────────────
// V2 per-ticker row = SignalWithDetails (full V2 signals row minus the raw
// `details` TEXT column, PLUS the parsed SignalDetailsV2 sibling). See
// shared/src/types/tracker-bundle.ts:64 — same shape api-worker brief-bundle
// emits, so the q-qa prompt sees the same per-ticker contract the dashboard
// renders against.
export type QQaTickerRow = SignalWithDetails;

export interface QQaInput {
  /** Today's date as ISO YYYY-MM-DD — for date-tense enforcement in the prompt. */
  readonly today_iso:       string;
  /** The single question being answered this call. */
  readonly question_id:     string;
  readonly question_text:   string;
  /** V2 macro synthesis output (m.synthesis market_emit row). Full passthrough
   *  payload — the renderer projects the load-bearing keys but tolerates
   *  additive fields written by future revisions. */
  readonly macro_synthesis: Record<string, unknown>;
  /** Per-ticker arbiter outputs — V2 narrow row + unmarshalled details sibling.
   *  One entry per signal (typically 5 portfolio tickers, possibly +N watch). */
  readonly per_ticker:      ReadonlyArray<QQaTickerRow>;
  /** Basket synthesis from a.1. */
  readonly basket:          Record<string, unknown>;
  /** Per-ticker asymmetry rank_rationale from a.1. Repurposed from the old
   *  ASYMMETRY SCAN zone — surfaces the "WHY this ticker has this asymmetry
   *  profile" prose as Q&A context. */
  readonly asymmetry_ranks: ReadonlyArray<{ ticker: string; rank_rationale: string }>;
  /** Phase 3 of news-coverage-design-2026-05-24-brief-revision.md — intra-day
   *  enrichment built by qa-worker fresh-context.ts. Optional: when absent
   *  or when tickers_extracted is empty, the renderer emits a "(no fresh
   *  context — question time data unavailable)" sentinel and the prompt
   *  falls back to the brief-only path. */
  readonly fresh_context?: {
    readonly tickers_extracted: ReadonlyArray<string>;
    readonly quote_deltas:      ReadonlyArray<{
      readonly ticker:        string;
      readonly current_price: number | null;   // live oracle consensus (was: Polygon daily bar)
      readonly as_of:         string | null;   // observed_at of that price (was: close_date)
      readonly pct_vs_signal: number | null;
    }>;
    readonly news:              ReadonlyArray<LlmNewsItem>;
    readonly earnings:          ReadonlyArray<{
      readonly ticker:            string;
      readonly next_date:         string | null;
      readonly last_surprise_pct: number | null;
    }>;
    readonly errors:            ReadonlyArray<string>;
  };
  /** §9b (per-ticker-inductive-forecaster brief-rev) — the path-dependent
   *  inductive forecast(s), threaded so the LLM can emit the TRAJECTORY
   *  perspective. Built by qa-worker from inductive_forecasts (market) +
   *  per_ticker_inductive_forecasts. Optional: when absent (no market floor
   *  yet, or a legacy brief) the renderer emits a sentinel and the TRAJECTORY
   *  answer falls back to "insufficient trajectory data". Per
   *  [[feedback_no_numeric_anchors_to_llm]] the raw trajectory_confidence
   *  scalar is NEVER threaded — it is pre-banded to high/med/low by the
   *  builder; direction/change_pct/CI/trend_summary pass through (the same
   *  digested peer m.synthesis already consumes). */
  readonly trajectory_view?: {
    /** Market-level inductive forecast (inductive_forecasts.payload). Null
     *  when the market floor is unmet or the brief predates the forecaster. */
    readonly market: {
      readonly trend_summary:              string;
      readonly confidence_band:            'high' | 'med' | 'low';
      readonly predicted_observables:      ReadonlyArray<{
        readonly indicator:      string;
        readonly direction:      string;
        readonly change_pct:     number;
        readonly change_ci_low:  number;
        readonly change_ci_high: number;
      }>;
      readonly trajectory_breaks_detected: ReadonlyArray<string>;
    } | null;
    /** Per-ticker inductive forecasts, already filtered to the question's
     *  tickers when those are known. Empty pre-floor. */
    readonly per_ticker: ReadonlyArray<{
      readonly ticker:                    string;
      readonly direction:                 string;
      readonly change_pct:                number;
      readonly change_ci_low:             number;
      readonly change_ci_high:            number;
      readonly trend_summary:             string;
      readonly confidence_band:           'high' | 'med' | 'low';
      readonly trajectory_break_detected: boolean;
    }>;
  };
  /** #12 (long-horizon-thesis-2026-07-09-brief-revision.md §3.7 names q.1.qa an
   *  intended consumer) — the DURABLE Long-Horizon Thesis (LHT) backdrop: the
   *  persistent weeks-to-months structural stance per scope + the MARKET
   *  composite regime, built by qa-runner from `lht_state` filtered to the
   *  question's tickers ∪ the MARKET composite. It is NOT a fifth perspective —
   *  it is the multi-week regime CONTEXT the near-term snapshot + trajectory sit
   *  inside, so longer-horizon questions can anchor on the structural stance
   *  rather than extrapolating the near-term signal alone. Optional: absent when
   *  no lht_state row matches (never fabricated). Per
   *  [[feedback_no_numeric_anchors_to_llm]] the raw `gauge`/`t_stat` scalars are
   *  NEVER threaded — `confidence` is already a qualitative band; stance/regime
   *  labels are STATE; key-level + pillar `price_level` are raw prices (allowed). */
  readonly lht_thesis?: ReadonlyArray<{
    readonly scope:          string;                              // 'MARKET' composite | ticker (SPY/$ETH/…)
    readonly asset_class:    'equity' | 'crypto' | 'composite';
    readonly stance:         'uptrend' | 'downtrend' | 'range';   // durable structural stance (STATE)
    readonly regime_label:   string;                              // goldilocks/reflation/… (MARKET) or per-scope label (STATE)
    readonly thesis_text:    string;                              // narrated durable thesis
    readonly horizon_answer: string;                              // the "where over the LHT horizon" narration
    readonly since_date:     string;                              // YYYY-MM-DD the stance committed
    readonly under_review:   boolean;                             // true → the durable stance is contested / mid-flip
    readonly confidence:     'low' | 'med' | 'high';              // already a qualitative band (safe to thread)
    readonly pillars:        ReadonlyArray<{
      readonly claim:        string;
      readonly falsifier:    string;
      readonly status:       'intact' | 'stressed' | 'broken';
      readonly price_level:  number | null;                       // raw price tripwire when checkable (allowed)
    }>;
    readonly key_levels:     { readonly invalidation: number | null; readonly reclaim: number | null };
  }>;
}

// ─── Render helpers ──────────────────────────────────────────────────────
function fmt(v: unknown): string {
  if (v == null) return 'null';
  if (typeof v === 'string') return v.length === 0 ? '""' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

function renderMacroSynthesis(m: Record<string, unknown>): string {
  // The fields most load-bearing for Q&A answers. m.synthesis output writes
  // thesis_headline/paragraph + basis{evidence,catalyst,invalidation} +
  // probability/horizon/conditional + cascade_prose + crux/pushback +
  // agreement_matrix + macro_predictions + regime/regime_confidence +
  // top_risk/risk_mit + weights + thesis_status/div. Passthrough keys
  // (e.g. cascade_nodes, cross_portfolio_thread) render via fmt for any
  // we list explicitly; future additive output fields stay decodable in
  // the payload but unrendered until added here.
  const keys = [
    'thesis_headline', 'thesis_paragraph',
    'crux', 'pushback',
    'basis',
    'probability', 'horizon', 'conditional',
    'cascade_prose', 'cascade_nodes', 'cross_portfolio_thread',
    'top_risk', 'risk_mit',
    'thesis_status', 'thesis_div',
    'regime', 'regime_confidence',
    'contrarian_weight', 'consensus_weight', 'inductive_weight',
    'weight_rationale',
    'agreement_matrix',
    'macro_predictions',
  ];
  return keys.map((k) => `  ${k}: ${fmt(m[k])}`).join('\n');
}

function renderTickerRow(t: QQaTickerRow): string {
  // V2 SignalWithDetails projection. Dense multi-line so Flash can scan
  // the whole basket structurally. Key fields the Q&A reasoning anchors on:
  // direction+magnitude · confidence (+bucket) · path_type · prediction_mode ·
  // thesis_align · winning_side · weights · catalyst (date+CI+prob) ·
  // invalidation+trigger · key_levels · momentum · ticker_paradox ·
  // reasoning + mechanism prose.
  const d = t.details;
  // S-09: pivot is the LIVE key-level field (pp/S1/S2/R1/R2, post-D40); support/resistance
  // are legacy arrays the writer no longer emits (always [] on current signals). Render
  // pivot; fall back to the legacy arrays only if a pre-D40 row still carries them.
  const piv = d.key_levels && 'pivot' in d.key_levels ? d.key_levels.pivot ?? null : null;
  const sup = d.key_levels?.support ?? [];
  const res = d.key_levels?.resistance ?? [];
  const mom = d.momentum ?? { rsi_14: null, macd_histogram: null, rsi_14_trend: null };
  const w   = d.weights ?? { contrarian: 0, consensus: 0 };
  // Option X (per-ticker-inductive-forecaster §4.2): when the floor-gated
  // p.inductive peer ran, weights are 3-way and winning_side can be 'inductive'.
  // Render the inductive slice only when present so pre-floor briefs read 2-way.
  const wInd = `inductive` in w && typeof (w as { inductive?: number }).inductive === 'number'
    ? `,i:${(w as { inductive?: number }).inductive}`
    : '';
  // V3 (PaceDial brief revision 2026-05-27): path_type/horizon_days/catalyst_date* dropped.
  // Use catalyst_label (structured), event_horizon_at (deadline), and event_horizon_conf/inv (levels).
  const head = `  ${t.ticker}: direction=${t.direction} · magnitude=${t.magnitude} · confidence=${t.confidence} (${t.confidence_bucket}) · catalyst_label=${t.catalyst_label || 'trend'} · prediction_mode=${t.prediction_mode} · timeframe=${t.timeframe} · event_horizon_at=${t.event_horizon_at} · thesis_align=${fmt(t.thesis_align)} · winning_side=${d.winning_side} · weights={c:${w.contrarian},s:${w.consensus}${wInd}}`;
  const px   = `    signal_price=${t.signal_price} · event_horizon_conf=${fmt(t.event_horizon_conf)} · event_horizon_inv=${fmt(t.event_horizon_inv)} · pct_upside=${fmt(t.pct_upside)} · pct_downside=${fmt(t.pct_downside)} · rr_ratio=${fmt(t.rr_ratio)} · ev_pct=${fmt(t.ev_pct)}`;
  const cat  = `    catalyst: ${fmt(d.catalyst)} · catalyst_probability=${fmt(d.catalyst_probability)}`;
  const inv  = `    invalidation_trigger: ${fmt(d.invalidation_trigger)}`;
  const lvl  = piv
    ? `    key_levels (pivot): pp=${fmt(piv.pp)} · S1=${fmt(piv.s1)} · S2=${fmt(piv.s2)} · R1=${fmt(piv.r1)} · R2=${fmt(piv.r2)}`
    : (sup.length > 0 || res.length > 0)
      ? `    key_levels (legacy): support=${JSON.stringify(sup)} resistance=${JSON.stringify(res)}`
      : `    key_levels: (none)`;
  const momLine = `    momentum: rsi_14=${fmt(mom.rsi_14)} (trend=${fmt(mom.rsi_14_trend)}) · macd_histogram=${fmt(mom.macd_histogram)}`;
  const para = d.ticker_paradox && d.ticker_paradox.length > 0
    ? `    ticker_paradox: ${d.ticker_paradox}`
    : `    ticker_paradox: (none)`;
  const mech = `    mechanism: ${fmt(d.mechanism)}`;
  const reas = `    reasoning: ${fmt(d.reasoning)}`;
  return [head, px, cat, inv, lvl, momLine, para, mech, reas].join('\n');
}

function renderBasket(b: Record<string, unknown>): string {
  return [
    `  bias_distribution: ${fmt(b['bias_distribution'])}`,
    `  alignment_per_ticker: ${fmt(b['alignment_per_ticker'])}`,
    `  cross_ticker_paradoxes: ${fmt(b['cross_ticker_paradoxes'])}`,
    `  synthesis_prose: ${fmt(b['synthesis_prose'])}`,
  ].join('\n');
}

function renderAsymmetryRanks(rs: ReadonlyArray<{ ticker: string; rank_rationale: string }>): string {
  if (rs.length === 0) return '  (none)';
  return rs.map((r) => `  ${r.ticker}: ${r.rank_rationale}`).join('\n');
}

function renderFreshContext(fc: NonNullable<QQaInput['fresh_context']>): string {
  if (fc.tickers_extracted.length === 0 && fc.news.length === 0) {
    return '  (no fresh context — no tickers detected and no macro headlines)';
  }
  const lines: string[] = [];
  if (fc.tickers_extracted.length > 0) {
    lines.push(`  TICKERS_DETECTED: ${fc.tickers_extracted.join(', ')}`);
  }
  if (fc.quote_deltas.length > 0) {
    lines.push(`  QUOTE_DELTAS:`);
    for (const q of fc.quote_deltas) {
      const price = q.current_price == null ? 'null' : q.current_price.toString();
      const asOf  = q.as_of ?? 'null';
      const pct   = q.pct_vs_signal == null
        ? 'null'
        : `${q.pct_vs_signal > 0 ? '+' : ''}${q.pct_vs_signal}%`;
      lines.push(`    ${q.ticker}: current_price=${price} (as_of ${asOf}) · ${pct} vs brief signal_price`);
    }
  }
  if (fc.news.length > 0) {
    lines.push(`  NEWS (last 48h, deduped + tier-ranked):`);
    for (let i = 0; i < fc.news.length; i++) {
      const it = fc.news[i]!;
      const tier   = `[${it.source_tier}]`;
      const date   = it.published_at_iso.slice(0, 16).replace('T', ' ');
      const summary = it.summary
        ? ` — ${it.summary.length > 180 ? it.summary.slice(0, 180) + '...' : it.summary}`
        : '';
      lines.push(`    [${i + 1}] ${tier} ${it.source_domain} (${date}): ${it.title}${summary}`);
    }
  } else {
    lines.push(`  NEWS: (no news in lookback window)`);
  }
  if (fc.earnings.length > 0) {
    lines.push(`  EARNINGS:`);
    for (const e of fc.earnings) {
      const next = e.next_date ?? 'null';
      const last = e.last_surprise_pct == null
        ? 'null'
        : `${e.last_surprise_pct > 0 ? '+' : ''}${e.last_surprise_pct}%`;
      lines.push(`    ${e.ticker}: next_date=${next} · last_surprise_pct=${last}`);
    }
  }
  if (fc.errors.length > 0) {
    lines.push(`  VENDOR_ERRORS: ${fc.errors.join('; ')}`);
  } else {
    lines.push(`  VENDOR_ERRORS: (none)`);
  }
  return lines.join('\n');
}

// §9b — render the path-dependent inductive forecast(s) as the substrate for
// the TRAJECTORY perspective. Confidence is already a qualitative band (the
// builder pre-bands the raw scalar per the no-numeric-anchors rule); direction
// /change_pct/CI/trend_summary pass through.
function renderTrajectoryView(tv: NonNullable<QQaInput['trajectory_view']>): string {
  if (tv.market === null && tv.per_ticker.length === 0) {
    return '  (no trajectory forecast available — market floor unmet or legacy brief; answer "insufficient trajectory data")';
  }
  const lines: string[] = [];
  if (tv.market !== null) {
    const m = tv.market;
    lines.push(`  MARKET (confidence band: ${m.confidence_band}):`);
    lines.push(`    trend_summary: ${m.trend_summary}`);
    if (m.predicted_observables.length > 0) {
      lines.push(`    predicted_observables:`);
      for (const o of m.predicted_observables) {
        lines.push(`      ${o.indicator}: ${o.direction} ${o.change_pct >= 0 ? '+' : ''}${o.change_pct}% (CI ${o.change_ci_low}..${o.change_ci_high})`);
      }
    }
    lines.push(`    trajectory_breaks_detected: ${m.trajectory_breaks_detected.length > 0 ? m.trajectory_breaks_detected.join(', ') : '(none)'}`);
  } else {
    lines.push(`  MARKET: (none)`);
  }
  if (tv.per_ticker.length > 0) {
    lines.push(`  PER_TICKER:`);
    for (const p of tv.per_ticker) {
      const brk = p.trajectory_break_detected ? ' · BREAK' : '';
      lines.push(`    ${p.ticker}: ${p.direction} ${p.change_pct >= 0 ? '+' : ''}${p.change_pct}% (CI ${p.change_ci_low}..${p.change_ci_high}) · band=${p.confidence_band}${brk} — ${p.trend_summary}`);
    }
  } else {
    lines.push(`  PER_TICKER: (none past history floor)`);
  }
  return lines.join('\n');
}

// #12 — render the durable LONG-HORIZON THESIS backdrop. The MARKET composite
// (regime + growth×inflation stance) renders first, then any per-scope theses.
// Numeric anchors (gauge/t_stat) are deliberately excluded upstream; confidence
// is the qualitative band; key-level + pillar price_level are raw prices.
function renderLhtThesis(lht: NonNullable<QQaInput['lht_thesis']>): string {
  if (lht.length === 0) {
    return '  (no long-horizon thesis available for the question\'s scopes — answer without a durable-regime anchor; do NOT fabricate one)';
  }
  // MARKET composite first (the regime backdrop), then per-scope alphabetically.
  const ordered = [...lht].sort((a, b) =>
    a.scope === 'MARKET' ? -1 : b.scope === 'MARKET' ? 1 : (a.scope < b.scope ? -1 : a.scope > b.scope ? 1 : 0));
  const lines: string[] = [];
  for (const t of ordered) {
    const review = t.under_review ? ' · UNDER_REVIEW (durable stance contested / mid-flip)' : '';
    lines.push(`  ${t.scope} [${t.asset_class}] — stance=${t.stance} · regime=${t.regime_label} · confidence=${t.confidence} · since=${t.since_date}${review}`);
    lines.push(`    thesis: ${t.thesis_text}`);
    if (t.horizon_answer && t.horizon_answer.length > 0) lines.push(`    horizon_view: ${t.horizon_answer}`);
    const kl: string[] = [];
    if (t.key_levels.invalidation != null) kl.push(`invalidation=${t.key_levels.invalidation}`);
    if (t.key_levels.reclaim != null) kl.push(`reclaim=${t.key_levels.reclaim}`);
    if (kl.length > 0) lines.push(`    key_levels: ${kl.join(' · ')}`);
    if (t.pillars.length > 0) {
      lines.push(`    pillars:`);
      for (const p of t.pillars) {
        const lvl = p.price_level != null ? ` (level=${p.price_level})` : '';
        lines.push(`      [${p.status}] ${p.claim} — falsifier: ${p.falsifier}${lvl}`);
      }
    }
  }
  return lines.join('\n');
}

// ─── buildUserPrompt ─────────────────────────────────────────────────────
export function buildUserPrompt(input: QQaInput): string {
  return `TODAY_ISO: ${input.today_iso}

QUESTION (answer this one only):
  id:   ${input.question_id}
  text: ${input.question_text}

MACRO SYNTHESIS (m.synthesis — path-adjudicated thesis + agreement matrix + cross-asset macro predictions):
${renderMacroSynthesis(input.macro_synthesis)}

PER-TICKER ARBITER OUTPUTS (${input.per_ticker.length} signals · V2 narrow + details):
${input.per_ticker.map(renderTickerRow).join('\n')}

BASKET SYNTHESIS (cross-ticker alignment + bias + paradoxes):
${renderBasket(input.basket)}

PER-TICKER ASYMMETRY RANK RATIONALES (a.1.synthesis "WHY this ticker has this asymmetry profile"):
${renderAsymmetryRanks(input.asymmetry_ranks)}

FRESH_CONTEXT (intra-day data pulled at question time — supersedes brief's frozen state where the values diverge):
${input.fresh_context ? renderFreshContext(input.fresh_context) : '  (FRESH_CONTEXT not built for this question — proceed using brief context only)'}

TRAJECTORY_VIEW (path-dependent inductive forecast — history-only, blind to today's payload; substrate for the TRAJECTORY perspective):
${input.trajectory_view ? renderTrajectoryView(input.trajectory_view) : '  (TRAJECTORY_VIEW not supplied — answer the trajectory perspective with "insufficient trajectory data")'}

LONG_HORIZON_THESIS (durable weeks-to-months structural regime backdrop — CONTEXT for every perspective, the multi-week anchor a longer-horizon answer sits inside; NOT a separate perspective):
${input.lht_thesis ? renderLhtThesis(input.lht_thesis) : '  (LONG_HORIZON_THESIS not supplied — no durable-regime anchor for these scopes; answer from the snapshot + trajectory only)'}

REQUEST: emit_qa_answer

Emit a SINGLE JSON object matching the OUTPUT SCHEMA — FOUR perspectives (consensus · contrarian · trajectory · predicted) PLUS /predicted/scoreable_claim. /question_id must echo "${input.question_id}" verbatim. /question_text_echo must echo the QUESTION text verbatim. Apply the RULES (GENERATE THE NUMBER AT THE ASKED HORIZON · CITATIONS MUST BE REAL · CONSENSUS AND CONTRARIAN MUST DIFFER · PREDICTED COMMITS · NO META · WORD LIMITS · STATE-ONLY · TRAJECTORY IS PATH-ONLY · SCOREABLE CLAIM MIRRORS /predicted) and the HORIZON + SCOREABLE CLAIM sections — answer at the horizon the QUESTION names, generating the estimate from the brief evidence; do NOT refuse over a horizon mismatch with the brief's near-term signal. Output JSON only.`;
}
